/**
 * Thread auth client backed by Keycloak OIDC.
 *
 * Backed by oidc-client-ts with Keycloak as the OIDC provider.
 *
 * Env vars:
 *   VITE_KEYCLOAK_URL   — e.g. http://localhost:8888 or https://auth.flexcompute.com
 *   VITE_KEYCLOAK_REALM — default: "thread"
 *   VITE_KEYCLOAK_CLIENT — default: "thread-web"
 */

import { UserManager, User, WebStorageStateStore } from "oidc-client-ts";

// ---------------------------------------------------------------------------
// Session type used by the app's auth consumers.
// ---------------------------------------------------------------------------

export type Session = {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  expires_at?: number;
  user: {
    id: string;
    email?: string;
    user_metadata: Record<string, unknown>;
    app_metadata: Record<string, unknown>;
    aud: string;
    created_at: string;
  };
};

// ---------------------------------------------------------------------------
// UserManager setup
// ---------------------------------------------------------------------------

const KEYCLOAK_URL = import.meta.env.VITE_KEYCLOAK_URL ?? "";
const REALM = import.meta.env.VITE_KEYCLOAK_REALM ?? "thread";
const CLIENT_ID = import.meta.env.VITE_KEYCLOAK_CLIENT ?? "thread-web";
const BASE_PATH = import.meta.env.BASE_URL ?? "/";
const IS_LOCAL = import.meta.env.VITE_THREAD_ENV === "local";
const API_BASE = import.meta.env.VITE_API_URL ?? "/api";
// Local-dev convenience: when VITE_THREAD_ENV=local AND this email is set, the
// editor auto-signs-in through the dev email magic-link flow so you never hit
// the sign-in wall locally. No-ops in any non-local build (see ensureLocalDevSession).
const LOCAL_DEV_EMAIL = (import.meta.env.VITE_LOCAL_DEV_EMAIL as string | undefined) ?? "";
const OIDC_ALLOWED_ORIGIN =
  import.meta.env.VITE_OIDC_ALLOWED_ORIGIN?.replace(/\/$/, "") ?? "";

export const userManager = new UserManager({
  authority: KEYCLOAK_URL
    ? `${KEYCLOAK_URL.replace(/\/$/, "")}/realms/${REALM}`
    : "http://localhost:8888/realms/thread",
  client_id: CLIENT_ID,
  redirect_uri: `${window.location.origin}${BASE_PATH.replace(/\/$/, "")}/auth/callback`,
  post_logout_redirect_uri: `${window.location.origin}${BASE_PATH}`,
  scope: "openid profile email",
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  automaticSilentRenew: false,
  loadUserInfo: false,
  // Keycloak issues tokens with the internal container URL as issuer; skip issuer check
  // so both local dev (http://localhost:8888) and prod (https://auth.flexcompute.com) work.
  filterProtocolClaims: false,
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function oidcUserToSession(user: User): Session {
  const profile = user.profile;
  return {
    access_token: user.access_token,
    refresh_token: user.refresh_token ?? "",
    token_type: user.token_type ?? "Bearer",
    expires_in: user.expires_in ?? 0,
    expires_at: user.expires_at,
    user: {
      id: String(profile.sub ?? ""),
      email: profile.email as string | undefined,
      user_metadata: {},
      app_metadata: {},
      aud: String(Array.isArray(profile.aud) ? profile.aud[0] : (profile.aud ?? CLIENT_ID)),
      created_at: new Date(0).toISOString(),
    },
  };
}

type AuthChangeEvent = "SIGNED_IN" | "SIGNED_OUT" | "TOKEN_REFRESHED";
type AuthStateChangeCallback = (event: AuthChangeEvent, session: Session | null) => void;

type EmailOtpParams = {
  email: string;
  options?: {
    emailRedirectTo?: string;
    data?: {
      presentation_slug?: string;
      presentationSlug?: string;
    };
  };
};

type EmailVerifyParams = {
  email: string;
  token: string;
  type?: "email" | string;
};

type EmailAuthStartResponse = {
  challenge_token: string;
  expires_in: number;
  delivery: string;
  dev_code?: string;
  dev_magic_link?: string;
};

type ThreadSessionResponse = {
  thread_session?: { access_token?: string; refresh_token?: string } | null;
  user?: { email?: string } | null;
};

const emailChallengeStorageKey = (email: string) =>
  `thread.email_auth.challenge.${email.trim().toLowerCase()}`;

type OAuthProvider = "google" | "github";

let sessionRenewal: Promise<User | null> | null = null;

async function removeStoredUser(): Promise<void> {
  try {
    await userManager.removeUser();
  } catch {
    // Best effort only: a failed cleanup should not block rendering the
    // signed-out state.
  }
}

function shouldHandoffOAuthToAllowedOrigin(): boolean {
  if (!OIDC_ALLOWED_ORIGIN || typeof window === "undefined") return false;
  if (window.location.origin === OIDC_ALLOWED_ORIGIN) return false;

  try {
    const allowedHost = new URL(OIDC_ALLOWED_ORIGIN).hostname;
    return (
      window.location.hostname === "thread.dev-simulation.cloud" &&
      allowedHost === "thread.simulation.cloud"
    );
  } catch {
    return false;
  }
}

function handoffOAuthToAllowedOrigin(provider: OAuthProvider): void {
  const allowed = new URL(OIDC_ALLOWED_ORIGIN);
  const target = new URL(window.location.href);
  target.protocol = allowed.protocol;
  target.host = allowed.host;
  target.searchParams.set("thread_auth_provider", provider);
  window.location.assign(target.toString());
}

async function readAuthError(response: Response): Promise<Error> {
  try {
    const body = await response.json();
    const detail = body?.detail;
    if (typeof detail === "string") return new Error(detail);
    return new Error(JSON.stringify(detail ?? body));
  } catch {
    return new Error(`Request failed (${response.status})`);
  }
}

function toAuthError(error: unknown): Error {
  const message = error instanceof Error ? error.message : String(error);
  if (
    message === "Load failed" ||
    message.includes("Failed to fetch") ||
    message.includes("NetworkError")
  ) {
    return new Error("Could not reach Thread sign-in. Check your network and try again.");
  }
  return error instanceof Error ? error : new Error(message || "Sign-in failed");
}

async function setThreadSessionFromResponse(data: ThreadSessionResponse): Promise<Error | null> {
  const threadSession = data.thread_session;
  if (!threadSession?.access_token || !threadSession?.refresh_token) {
    return new Error("No Thread session was returned");
  }
  const { error } = await authClient.auth.setSession({
    access_token: threadSession.access_token,
    refresh_token: threadSession.refresh_token,
  });
  return error;
}

async function renewStoredUser(): Promise<User | null> {
  sessionRenewal ??= userManager.signinSilent().catch(async () => {
    await removeStoredUser();
    return null;
  }).finally(() => {
    sessionRenewal = null;
  });
  return sessionRenewal;
}

async function getUsableUser(): Promise<User | null> {
  const user = await userManager.getUser();
  if (!user) return null;
  if (!user.expired) return user;
  if (!user.refresh_token) {
    await removeStoredUser();
    return null;
  }

  const renewed = await renewStoredUser();
  if (renewed && !renewed.expired) return renewed;
  if (renewed?.expired) {
    await removeStoredUser();
  }
  return null;
}

// Local-dev auto sign-in. Dedupes concurrent attempts. Strictly gated:
//   1. IS_LOCAL (VITE_THREAD_ENV=local) — never in dev/preprod/prod builds
//   2. LOCAL_DEV_EMAIL configured (VITE_LOCAL_DEV_EMAIL in .env.local)
//   3. the API returns a dev_code (only the local thread-api does this)
// On any failure it stays signed out, i.e. falls back to the normal wall.
let localDevSignIn: Promise<void> | null = null;
async function ensureLocalDevSession(): Promise<void> {
  if (!IS_LOCAL || !LOCAL_DEV_EMAIL) return;
  localDevSignIn ??= (async () => {
    try {
      const email = LOCAL_DEV_EMAIL.trim().toLowerCase();
      const startRes = await fetch(`${API_BASE}/auth/email/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!startRes.ok) return;
      const start: EmailAuthStartResponse = await startRes.json();
      if (!start.dev_code) return; // prod-safe: only the local API hands back a code
      const verifyRes = await fetch(`${API_BASE}/auth/email/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          code: start.dev_code,
          challenge_token: start.challenge_token,
        }),
      });
      if (!verifyRes.ok) return;
      await setThreadSessionFromResponse(await verifyRes.json());
    } catch {
      // best effort — leave the user signed out so the normal flow still works
    }
  })().finally(() => {
    localDevSignIn = null;
  });
  return localDevSignIn;
}

// ---------------------------------------------------------------------------
// Auth client
// ---------------------------------------------------------------------------

export const authClient = {
  auth: {
    async getSession(): Promise<{ data: { session: Session | null }; error: null }> {
      try {
        let user = await getUsableUser();
        if (!user && IS_LOCAL && LOCAL_DEV_EMAIL) {
          await ensureLocalDevSession();
          user = await getUsableUser();
        }
        if (!user) return { data: { session: null }, error: null };
        return { data: { session: oidcUserToSession(user) }, error: null };
      } catch {
        return { data: { session: null }, error: null };
      }
    },

    async signInWithOAuth({
      provider,
      options,
    }: {
      provider: OAuthProvider;
      options?: { redirectTo?: string; queryParams?: Record<string, string> };
    }): Promise<{ error: Error | null }> {
      if (shouldHandoffOAuthToAllowedOrigin()) {
        handoffOAuthToAllowedOrigin(provider);
        return { error: null };
      }

      const providerHint: Record<string, string> = {
        google: "google",
        github: "github",
      };
      try {
        await userManager.signinRedirect({
          redirect_uri: options?.redirectTo,
          extraQueryParams: IS_LOCAL
            ? undefined
            : {
                kc_idp_hint: providerHint[provider] ?? provider,
              },
        });
        return { error: null };
      } catch (error) {
        return { error: toAuthError(error) };
      }
    },

    async signOut(): Promise<void> {
      const user = await userManager.getUser();
      await userManager.removeUser();
      // Terminate Keycloak server-side session so the next login actually
      // shows the GitHub/Google consent page instead of auto-logging in.
      if (user?.id_token) {
        try {
          await userManager.signoutRedirect({ id_token_hint: user.id_token });
        } catch {
          // signoutRedirect navigates away — if it throws, fall back to reload
          window.location.href = BASE_PATH;
        }
      } else {
        // No OIDC session (e.g. Flow360 cookie flow) — reload so the app
        // re-evaluates auth state from scratch and shows the sign-in page.
        window.location.href = BASE_PATH;
      }
    },

    onAuthStateChange(callback: AuthStateChangeCallback): {
      data: { subscription: { unsubscribe: () => void } };
    } {
      const onLoaded = (user: User) => {
        callback("SIGNED_IN", oidcUserToSession(user));
      };
      const onUnloaded = () => {
        callback("SIGNED_OUT", null);
      };
      const onExpired = () => {
        void renewStoredUser().then((user) => {
          if (user && !user.expired) {
            callback("TOKEN_REFRESHED", oidcUserToSession(user));
          } else {
            callback("SIGNED_OUT", null);
          }
        });
      };

      userManager.events.addUserLoaded(onLoaded);
      userManager.events.addUserUnloaded(onUnloaded);
      userManager.events.addAccessTokenExpired(onExpired);

      return {
        data: {
          subscription: {
            unsubscribe() {
              userManager.events.removeUserLoaded(onLoaded);
              userManager.events.removeUserUnloaded(onUnloaded);
              userManager.events.removeAccessTokenExpired(onExpired);
            },
          },
        },
      };
    },

    /**
     * Store a session minted externally (e.g. by the Flow360 backend token exchange).
     * Decodes the JWT payload to extract user profile and expiry.
     */
    async setSession({
      access_token,
      refresh_token,
    }: {
      access_token: string;
      refresh_token: string;
    }): Promise<{ error: Error | null }> {
      try {
        const parts = access_token.split(".");
        if (parts.length !== 3) throw new Error("Invalid JWT format");
        const payloadBase64 = parts[1]!;
        const payload = JSON.parse(atob(payloadBase64.replace(/-/g, "+").replace(/_/g, "/")));

        const user = new User({
          access_token,
          refresh_token,
          token_type: "Bearer",
          scope: "openid profile email",
          profile: {
            sub: String(payload.sub ?? ""),
            iss: String(payload.iss ?? ""),
            aud: payload.aud ?? CLIENT_ID,
            exp: Number(payload.exp ?? 0),
            iat: Number(payload.iat ?? 0),
            email: payload.email,
          },
          expires_at: Number(payload.exp ?? 0),
        });

        await userManager.storeUser(user);
        // Notify all onAuthStateChange listeners
        await userManager.events.load(user);
        return { error: null };
      } catch (err) {
        return { error: err instanceof Error ? err : new Error(String(err)) };
      }
    },

    async signInWithOtp(params: EmailOtpParams): Promise<{
      data?: EmailAuthStartResponse;
      error: Error | null;
    }> {
      try {
        const email = params.email.trim().toLowerCase();
        const presentationSlug =
          params.options?.data?.presentation_slug ?? params.options?.data?.presentationSlug;
        const response = await fetch(`${API_BASE}/auth/email/start`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email,
            presentation_slug: presentationSlug,
            redirect_to: params.options?.emailRedirectTo,
          }),
        });
        if (!response.ok) return { error: await readAuthError(response) };

        const data: EmailAuthStartResponse = await response.json();
        sessionStorage.setItem(
          emailChallengeStorageKey(email),
          JSON.stringify({
            challenge_token: data.challenge_token,
            expires_at: Date.now() + data.expires_in * 1000,
          }),
        );
        if (data.dev_code) {
          console.info("[email-auth] Local dev code:", data.dev_code);
        }
        if (data.dev_magic_link) {
          console.info("[email-auth] Local dev magic link:", data.dev_magic_link);
        }
        return { data, error: null };
      } catch (err) {
        return { error: err instanceof Error ? err : new Error(String(err)) };
      }
    },

    async verifyOtp(params: EmailVerifyParams): Promise<{ error: Error | null }> {
      try {
        const email = params.email.trim().toLowerCase();
        const stored = sessionStorage.getItem(emailChallengeStorageKey(email));
        const challenge = stored ? JSON.parse(stored) : null;
        if (!challenge?.challenge_token) {
          return { error: new Error("No email sign-in challenge was found. Request a new code.") };
        }

        const response = await fetch(`${API_BASE}/auth/email/verify`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email,
            code: params.token,
            challenge_token: challenge.challenge_token,
          }),
        });
        if (!response.ok) return { error: await readAuthError(response) };

        const data: ThreadSessionResponse = await response.json();
        const sessionError = await setThreadSessionFromResponse(data);
        if (sessionError) return { error: sessionError };
        sessionStorage.removeItem(emailChallengeStorageKey(email));
        return { error: null };
      } catch (err) {
        return { error: err instanceof Error ? err : new Error(String(err)) };
      }
    },

    async verifyEmailMagicLink(token: string): Promise<{ error: Error | null }> {
      try {
        const response = await fetch(`${API_BASE}/auth/email/verify`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ magic_token: token }),
        });
        if (!response.ok) return { error: await readAuthError(response) };

        const data: ThreadSessionResponse = await response.json();
        const sessionError = await setThreadSessionFromResponse(data);
        if (sessionError) return { error: sessionError };
        return { error: null };
      } catch (err) {
        return { error: err instanceof Error ? err : new Error(String(err)) };
      }
    },
  },
};
